import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import LanguageSelector from './LanguageSelector';

const Navbar = ({ onToggleSidebar, sidebarCollapsed, toggleButtonRef }) => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const userButtonRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (open && menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        userButtonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    window.requestAnimationFrame(() => menuRef.current?.querySelector('.dropdown-item')?.focus());
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const handleLogout = () => {
    logout();
  };

  return (
    <nav className="app-navbar d-flex align-items-center px-3 px-lg-4" aria-label={t('navbar.primaryNavigation')}>
      <div className="d-flex align-items-center gap-3">
        <button
          type="button"
          ref={toggleButtonRef}
          className="app-icon-button sidebar-toggle-btn"
          onClick={onToggleSidebar}
          aria-label={sidebarCollapsed ? t('navbar.expandMenu') : t('navbar.collapseMenu')}
          aria-expanded={!sidebarCollapsed}
          aria-controls="primary-navigation"
        >
          <i className="bi bi-layout-sidebar" aria-hidden="true"></i>
        </button>
        <div className="navbar-brand d-flex align-items-center gap-2">
          <span className="navbar-brand-mark" aria-hidden="true">
            <i className="bi bi-journal-bookmark-fill"></i>
          </span>
          <span className="d-flex flex-column">
            <span className="navbar-brand-text">{t('app.name')}</span>
            <span className="navbar-brand-tagline d-none d-xl-block">{t('app.tagline')}</span>
          </span>
        </div>
      </div>
      <div className="ms-auto d-flex align-items-center gap-3">
        <LanguageSelector />
        {user && (
          <div className="dropdown" ref={menuRef}>
            <button
              type="button"
              ref={userButtonRef}
              className="btn d-flex align-items-center gap-2 user-btn"
              onClick={() => setOpen(o => !o)}
              aria-expanded={open}
              aria-haspopup="menu"
              aria-controls="user-menu"
            >
              <span className="avatar-circle bg-primary text-white">{user.email.charAt(0).toUpperCase()}</span>
              <span className="user-email d-none d-sm-inline small fw-medium">{user.email}</span>
              <i className="bi bi-chevron-down small" aria-hidden="true"></i>
            </button>
            {open && (
              <div id="user-menu" className="dropdown-menu dropdown-menu-end show shadow-sm mt-2 py-2 user-menu" role="menu">
                <div className="px-3 pb-2 small text-muted border-bottom mb-2" style={{maxWidth:220}}>
                  {t('navbar.loggedInAs')}<br /><span className="text-dark fw-semibold">{user.email}</span>
                </div>
                <button className="dropdown-item" type="button" onClick={handleLogout} role="menuitem">
                  <i className="bi bi-box-arrow-right me-2" aria-hidden="true"></i>{t('navbar.logout')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
